// Model lifecycle helpers — the single source of truth for how the three
// lifecycle states are labelled, coloured and compared across the UI.
//
// The backend (ModelDescriptor.LifecycleState) serializes as a camelCase enum:
// "active" | "deprecated" | "retired". They happen to be single words, so they
// arrive lowercase — but C# enum wire-casing has bitten us before, so always
// compare case-insensitively (see CLAUDE.md / memory: enum casing).
//
// Terminology: a *retired* model is surfaced to users as "Unavailable" — it is
// no longer callable at the provider and is kept only for historic reference.
// A *deprecated* model is still callable, just discouraged (migrate when able).

export const LIFECYCLE = {
  active: { color: "green", label: "Active" },
  deprecated: { color: "yellow", label: "Deprecated" },
  retired: { color: "gray", label: "Unavailable" },
};

export function lifecycleOf(model) {
  return model?.lifecycleState?.toLowerCase() || "active";
}

export function lifecycleMeta(model) {
  return LIFECYCLE[lifecycleOf(model)] ?? LIFECYCLE.active;
}

export function isActive(model) {
  return lifecycleOf(model) === "active";
}

// Still callable, but customers should migrate before the sunset date.
export function isDeprecated(model) {
  return lifecycleOf(model) === "deprecated";
}

// Retired at the provider — no longer callable. Historic value only, so it
// gets its own guard (hidden by default; never offered as a usable target).
export function isUnavailable(model) {
  return lifecycleOf(model) === "retired";
}
